import React, { useState, useEffect } from 'react';
import { Loader2, FileText, Save, Edit2, X, Check, AlertCircle, Tag, ChevronDown, ChevronUp, CheckCircle, Calendar } from 'lucide-react';

const BLOG_TYPES = [
  "3 Things You Need to Know About…",
  "5 Mistakes Investors Always Make With…",
  "In the News: What This Means for Property Investors",
  "The Ultimate Guide to…",
  "YPN Deep Dive: Everything We've Learned About…",
  "Beginner's Blueprint: How to Get Started With…",
  "Advanced Strategies: How Experienced Investors Use…",
  "Case Study Breakdown: What This Investor Did Right (and Wrong)",
  "The Truth About… (What Investors Often Get Wrong)",
  "Industry Snapshot: The State of the Market in <Month/Year>",
  "Step-by-Step: How to Do <X> Safely and Profitably",
  "Pros and Cons of <Strategy>",
  "Comparison: JV vs Angel Finance vs Raising Private Money",
  "The Investor's Checklist for…",
  "YPN Curated Highlights: What the Last 10 Years of Articles Say About…",
  "Before You Start: 7 Questions to Ask Yourself About…",
  "In Plain English: Explaining <Complex Topic> Simply",
  "Micro-Guide: A Quick Explanation of…",
  "The Top 5 Tools/Resources for…",
  "What No One Tells You About…",
  "If I Were Starting Again: How I'd Approach…"
];

const BlogsPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState({});
  const [approving, setApproving] = useState({});
  const [publishing, setPublishing] = useState({});
  const [showPublishModal, setShowPublishModal] = useState(null); // blogId or null
  const [publishDate, setPublishDate] = useState('');
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    category: '',
    seo_phrases: '',
    blog_type: ''
  });
  
  // Generated blog state
  const [generatedBlog, setGeneratedBlog] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [editedTitle, setEditedTitle] = useState('');

  // Category selection state
  const [categories, setCategories] = useState({}); // { main_category: [sub_categories] }
  const [selectedMainCategory, setSelectedMainCategory] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]); // [{ main: string, sub: string }]
  const [categoriesExpanded, setCategoriesExpanded] = useState(false);
  
  // Articles selection state
  const [articlesByEdition, setArticlesByEdition] = useState({}); // { edition: [articles] }
  const [selectedEdition, setSelectedEdition] = useState(null);
  const [selectedArticles, setSelectedArticles] = useState([]); // [{ id: number, title: string, edition: string }]
  const [articlesExpanded, setArticlesExpanded] = useState(false);

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
    fetchArticlesByEdition();
  }, []);

  const fetchArticlesByEdition = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/articles/by-edition`
      );
      const data = await response.json();
      
      if (data.success) {
        setArticlesByEdition(data.articles_by_edition || {});
      }
    } catch (error) {
      console.error('Error fetching articles by edition:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/categories`
      );
      const data = await response.json();
      
      if (data.success) {
        setCategories(data.categories || {});
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/blogs`
      );
      const data = await response.json();
      
      if (data.success) {
        setBlogs(data.blogs || []);
      } else {
        setError('Failed to fetch blogs');
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setError('Error loading blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleMainCategoryClick = (mainCategory) => {
    setSelectedMainCategory(mainCategory);
  };

  const handleSubCategoryClick = (mainCategory, subCategory) => {
    // Check if this combination is already selected
    const exists = selectedCategories.some(
      cat => cat.main === mainCategory && cat.sub === subCategory
    );
    
    if (!exists) {
      // Add both main and sub category
      setSelectedCategories(prev => [
        ...prev,
        { main: mainCategory, sub: subCategory }
      ]);
    }
  };

  const handleRemoveCategory = (index) => {
    setSelectedCategories(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditionClick = (edition) => {
    setSelectedEdition(edition);
  };

  const handleArticleClick = (article, edition) => {
    // Check if this article is already selected
    const exists = selectedArticles.some(
      art => art.id === article.id
    );
    
    if (!exists) {
      setSelectedArticles(prev => [
        ...prev,
        { id: article.id, title: article.title, edition: edition }
      ]);
    }
  };

  const handleRemoveArticle = (index) => {
    setSelectedArticles(prev => prev.filter((_, i) => i !== index));
  };

  const handleGenerateBlog = async () => {
    if (!formData.category || !formData.seo_phrases || !formData.blog_type) {
      alert('Please fill in all fields');
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      
      const requestBody = {
        ...formData,
        selected_categories: selectedCategories, // Include selected categories
        selected_articles: selectedArticles.map(a => a.id) // Include selected article IDs
      };
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/blogs/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `Server error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setGeneratedBlog(data.blog);
        setEditedContent(data.blog.content);
        setEditedTitle(data.blog.title || '');
        // Reset form
        setFormData({ category: '', seo_phrases: '', blog_type: '' });
      } else {
        throw new Error(data.message || 'Failed to generate blog');
      }
    } catch (error) {
      console.error('Error generating blog:', error);
      setError('Error generating blog: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveBlog = async (blogId = null) => {
    const contentToSave = blogId ? editedContent : generatedBlog?.content;
    const titleToSave = blogId ? editedTitle : generatedBlog?.title;
    const blogToSave = blogId ? blogs.find(b => b.id === blogId) : generatedBlog;

    if (!contentToSave) {
      alert('No content to save');
      return;
    }

    try {
      setSaving(prev => ({ ...prev, [blogId || 'new']: true }));
      
      const url = blogId 
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/blogs/${blogId}`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/blogs`;
      
      const method = blogId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category: blogToSave?.category || formData.category,
          seo_phrases: blogToSave?.seo_phrases || formData.seo_phrases,
          blog_type: blogToSave?.blog_type || formData.blog_type,
          title: titleToSave || 'Untitled',
          content: contentToSave
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `Server error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        if (blogId) {
          // Update existing blog in list
          setBlogs(prev => prev.map(b => b.id === blogId ? data.blog : b));
          setEditingId(null);
        } else {
          // Add new blog to list
          setBlogs(prev => [data.blog, ...prev]);
          setGeneratedBlog(null);
          setEditedContent('');
          setEditedTitle('');
        }
        alert('Blog saved successfully!');
      } else {
        throw new Error(data.message || 'Failed to save blog');
      }
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Error saving blog: ' + error.message);
    } finally {
      setSaving(prev => ({ ...prev, [blogId || 'new']: false }));
    }
  };

  const handleEdit = (blog) => {
    setEditingId(blog.id);
    setEditedContent(blog.content);
    setEditedTitle(blog.title || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedContent('');
    setEditedTitle('');
  };

  const handleApprove = async (blogId) => {
    try {
      setApproving(prev => ({ ...prev, [blogId]: true }));
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/blogs/${blogId}/approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `Server error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setBlogs(prev => prev.map(b => b.id === blogId ? data.blog : b));
        alert('Blog approved successfully!');
      } else {
        throw new Error(data.message || 'Failed to approve blog');
      }
    } catch (error) {
      console.error('Error approving blog:', error);
      alert('Error approving blog: ' + error.message);
    } finally {
      setApproving(prev => ({ ...prev, [blogId]: false }));
    }
  };

  const handlePublishClick = (blogId) => {
    setShowPublishModal(blogId);
    // Set default date to today
    const today = new Date().toISOString().split('T')[0];
    setPublishDate(today);
  };

  const handlePublish = async (blogId) => {
    if (!publishDate) {
      alert('Please select a publish date');
      return;
    }

    try {
      setPublishing(prev => ({ ...prev, [blogId]: true }));
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/blogs/${blogId}/publish`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            published_date: publishDate
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `Server error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setBlogs(prev => prev.map(b => b.id === blogId ? data.blog : b));
        setShowPublishModal(null);
        setPublishDate('');
        alert('Blog published successfully!');
      } else {
        throw new Error(data.message || 'Failed to publish blog');
      }
    } catch (error) {
      console.error('Error publishing blog:', error);
      alert('Error publishing blog: ' + error.message);
    } finally {
      setPublishing(prev => ({ ...prev, [blogId]: false }));
    }
  };

  // Render markdown content with clickable links
  const renderMarkdownContent = (content) => {
    if (!content) return null;
    
    // Split content into lines
    const lines = content.split('\n');
    const elements = [];
    
    lines.forEach((line, lineIndex) => {
      // Check if line is a heading first
      if (line.startsWith('## ')) {
        const headingText = line.replace(/^##\s+/, '');
        // Process heading for links
        const headingElements = [];
        const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        let lastIndex = 0;
        let match;
        
        while ((match = linkRegex.exec(headingText)) !== null) {
          if (match.index > lastIndex) {
            headingElements.push(headingText.substring(lastIndex, match.index));
          }
          headingElements.push(
            <a
              key={`heading-link-${lineIndex}-${match.index}`}
              href={match[2]}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 hover:text-purple-800 underline"
            >
              {match[1]}
            </a>
          );
          lastIndex = match.index + match[0].length;
        }
        if (lastIndex < headingText.length) {
          headingElements.push(headingText.substring(lastIndex));
        }
        
        elements.push(
          <h2 key={lineIndex} className="text-xl font-bold text-gray-900 mt-6 mb-3">
            {headingElements.length > 0 ? headingElements : headingText}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        const headingText = line.replace(/^###\s+/, '');
        // Process heading for links
        const headingElements = [];
        const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        let lastIndex = 0;
        let match;
        
        while ((match = linkRegex.exec(headingText)) !== null) {
          if (match.index > lastIndex) {
            headingElements.push(headingText.substring(lastIndex, match.index));
          }
          headingElements.push(
            <a
              key={`subheading-link-${lineIndex}-${match.index}`}
              href={match[2]}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 hover:text-purple-800 underline"
            >
              {match[1]}
            </a>
          );
          lastIndex = match.index + match[0].length;
        }
        if (lastIndex < headingText.length) {
          headingElements.push(headingText.substring(lastIndex));
        }
        
        elements.push(
          <h3 key={lineIndex} className="text-lg font-semibold text-gray-900 mt-4 mb-2">
            {headingElements.length > 0 ? headingElements : headingText}
          </h3>
        );
      } else if (line.trim() === '') {
        elements.push(<br key={lineIndex} />);
      } else {
        // Process line for markdown links [text](url)
        const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        let lastIndex = 0;
        let match;
        const lineElements = [];
        let hasLinks = false;
        
        // Reset regex lastIndex
        linkRegex.lastIndex = 0;
        
        while ((match = linkRegex.exec(line)) !== null) {
          hasLinks = true;
          // Add text before the link
          if (match.index > lastIndex) {
            lineElements.push(line.substring(lastIndex, match.index));
          }
          // Add the link
          lineElements.push(
            <a
              key={`link-${lineIndex}-${match.index}`}
              href={match[2]}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 hover:text-purple-800 underline"
            >
              {match[1]}
            </a>
          );
          lastIndex = match.index + match[0].length;
        }
        
        // Add remaining text after last link
        if (lastIndex < line.length) {
          lineElements.push(line.substring(lastIndex));
        }
        
        // If no links found, just add the line as-is
        if (!hasLinks) {
          lineElements.push(line);
        }
        
        elements.push(
          <p key={lineIndex} className="mb-2">
            {lineElements}
          </p>
        );
      }
    });
    
    return <div>{elements}</div>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Blogs</h1>
        <p className="mt-2 text-gray-600">
          Create and manage AI-generated blog articles for YPN
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Create New Blog Form */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Create New Blog</h2>
        
        <div className="space-y-4">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Top Level Category
            </label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              placeholder="e.g., Money, Finance & Funding"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          {/* SEO Phrases */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SEO Related Phrases
            </label>
            <textarea
              value={formData.seo_phrases}
              onChange={(e) => setFormData(prev => ({ ...prev, seo_phrases: e.target.value }))}
              placeholder="e.g., How much money do I need to get started? Can I invest in property with little or none of my own money?"
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          {/* Blog Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Blog Type
            </label>
            <select
              value={formData.blog_type}
              onChange={(e) => setFormData(prev => ({ ...prev, blog_type: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">Select a blog type...</option>
              {BLOG_TYPES.map((type, index) => (
                <option key={index} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Category Selection - Collapsible */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setCategoriesExpanded(!categoriesExpanded)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Focus Categories (Optional)
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Select categories to help AI focus on relevant articles
                </p>
              </div>
              {categoriesExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            
            {categoriesExpanded && (
              <div className="p-4 pt-0 border-t border-gray-200">
                {/* Row 1: Main Categories */}
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Main Categories:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(categories).map((mainCat) => (
                      <button
                        key={mainCat}
                        type="button"
                        onClick={() => handleMainCategoryClick(mainCat)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          selectedMainCategory === mainCat
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {mainCat}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Row 2: Sub Categories (shown when main category is selected) */}
                {selectedMainCategory && categories[selectedMainCategory] && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Sub Categories for "{selectedMainCategory}":
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {categories[selectedMainCategory].map((subCat) => (
                        <button
                          key={subCat.id}
                          type="button"
                          onClick={() => handleSubCategoryClick(selectedMainCategory, subCat.sub_category)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            selectedCategories.some(
                              cat => cat.main === selectedMainCategory && cat.sub === subCat.sub_category
                            )
                              ? 'bg-green-600 text-white'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                        >
                          {subCat.sub_category}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Categories Builder */}
                {selectedCategories.length > 0 && (
                  <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">Selected Categories:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedCategories.map((cat, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center space-x-2 px-3 py-1.5 bg-purple-100 text-purple-800 rounded-lg text-sm"
                        >
                          <Tag className="w-4 h-4" />
                          <span>
                            <span className="font-semibold">{cat.main}</span>
                            {cat.sub && <span className="text-purple-600"> → {cat.sub}</span>}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveCategory(index)}
                            className="ml-2 text-purple-600 hover:text-purple-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Articles Selection - Collapsible */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setArticlesExpanded(!articlesExpanded)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Articles (Optional)
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Select specific articles to be referenced in the blog
                </p>
              </div>
              {articlesExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            
            {articlesExpanded && (
              <div className="p-4 pt-0 border-t border-gray-200">
                {/* Row 1: Editions */}
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Editions:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(articlesByEdition).map((edition) => (
                      <button
                        key={edition}
                        type="button"
                        onClick={() => handleEditionClick(edition)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          selectedEdition === edition
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {edition}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Row 2: Articles (shown when edition is selected) */}
                {selectedEdition && articlesByEdition[selectedEdition] && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Articles in "{selectedEdition}":
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {articlesByEdition[selectedEdition].map((article) => (
                        <button
                          key={article.id}
                          type="button"
                          onClick={() => handleArticleClick(article, selectedEdition)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            selectedArticles.some(art => art.id === article.id)
                              ? 'bg-green-600 text-white'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                        >
                          {article.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Articles Builder */}
                {selectedArticles.length > 0 && (
                  <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">Selected Articles:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedArticles.map((article, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center space-x-2 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-lg text-sm"
                        >
                          <FileText className="w-4 h-4" />
                          <span>
                            <span className="font-semibold">{article.edition}</span>
                            <span className="text-blue-600"> → {article.title}</span>
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveArticle(index)}
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Generate Button */}
          <div className="flex justify-end">
            <button
              onClick={handleGenerateBlog}
              disabled={generating || !formData.category || !formData.seo_phrases || !formData.blog_type}
              className="px-6 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Generating Blog...</span>
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  <span>Create Blog</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Generated Blog Preview/Editor */}
      {generatedBlog && (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Generated Blog</h2>
            <div className="flex space-x-2">
              <button
                onClick={() => handleSaveBlog()}
                disabled={saving['new']}
                className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {saving['new'] ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save Blog</span>
                  </>
                )}
              </button>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              rows={20}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
            />
            {/* Preview of Sources section if it exists */}
            {editedContent.includes('## Sources') && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs font-medium text-blue-900 mb-2">Sources Preview:</p>
                <div className="text-sm text-gray-700">
                  {renderMarkdownContent(editedContent.split('## Sources')[1] ? '## Sources' + editedContent.split('## Sources')[1] : '')}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Saved Blogs List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Saved Blogs</h2>
        </div>
        
        {blogs.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No blogs yet</h3>
            <p className="text-gray-500">Create your first blog using the form above</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {blogs.map((blog) => (
              <div key={blog.id} className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {editingId === blog.id ? (
                        <input
                          type="text"
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                          className="w-full px-3 py-1 border border-gray-300 rounded-lg"
                        />
                      ) : (
                        blog.title || 'Untitled'
                      )}
                    </h3>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-600 mb-2">
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded">
                        {blog.category}
                      </span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                        {blog.blog_type}
                      </span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded">
                        {blog.word_count || 0} words
                      </span>
                      <span className={`px-2 py-1 rounded ${
                        blog.status === 'published' ? 'bg-green-100 text-green-800' :
                        blog.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {blog.status || 'draft'}
                      </span>
                      {blog.published_date && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                          Published: {new Date(blog.published_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      Created: {new Date(blog.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    {editingId === blog.id ? (
                      <>
                        <button
                          onClick={() => handleSaveBlog(blog.id)}
                          disabled={saving[blog.id]}
                          className="px-3 py-1 text-sm text-white bg-green-600 rounded hover:bg-green-700 transition-colors disabled:bg-gray-400 flex items-center space-x-1"
                        >
                          {saving[blog.id] ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          <span>Save</span>
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-3 py-1 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors flex items-center space-x-1"
                        >
                          <X className="w-4 h-4" />
                          <span>Cancel</span>
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEdit(blog)}
                          className="px-3 py-1 text-sm text-purple-600 bg-purple-50 rounded hover:bg-purple-100 transition-colors flex items-center space-x-1"
                        >
                          <Edit2 className="w-4 h-4" />
                          <span>Edit</span>
                        </button>
                        {blog.status === 'draft' && (
                          <button
                            onClick={() => handleApprove(blog.id)}
                            disabled={approving[blog.id]}
                            className="px-3 py-1 text-sm text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors flex items-center space-x-1 disabled:bg-gray-200 disabled:text-gray-400"
                          >
                            {approving[blog.id] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                            <span>Approve</span>
                          </button>
                        )}
                        {blog.status === 'approved' && (
                          <button
                            onClick={() => handlePublishClick(blog.id)}
                            disabled={publishing[blog.id]}
                            className="px-3 py-1 text-sm text-green-600 bg-green-50 rounded hover:bg-green-100 transition-colors flex items-center space-x-1 disabled:bg-gray-200 disabled:text-gray-400"
                          >
                            {publishing[blog.id] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Calendar className="w-4 h-4" />
                            )}
                            <span>Publish</span>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                
                {/* Publish Date Modal */}
                {showPublishModal === blog.id && (
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Set Publish Date</h4>
                    <div className="flex items-center space-x-2">
                      <input
                        type="date"
                        value={publishDate}
                        onChange={(e) => setPublishDate(e.target.value)}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
                      />
                      <button
                        onClick={() => handlePublish(blog.id)}
                        disabled={publishing[blog.id] || !publishDate}
                        className="px-3 py-1 text-sm text-white bg-green-600 rounded hover:bg-green-700 transition-colors disabled:bg-gray-400 flex items-center space-x-1"
                      >
                        {publishing[blog.id] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                        <span>Confirm Publish</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowPublishModal(null);
                          setPublishDate('');
                        }}
                        className="px-3 py-1 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors flex items-center space-x-1"
                      >
                        <X className="w-4 h-4" />
                        <span>Cancel</span>
                      </button>
                    </div>
                  </div>
                )}
                
                {editingId === blog.id ? (
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    rows={15}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
                  />
                ) : (
                  <div className="prose max-w-none">
                    <div className="text-sm text-gray-700 max-h-96 overflow-y-auto">
                      {renderMarkdownContent(blog.content)}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogsPage;

